import _ from 'lodash';
import produce from 'immer';
import { Transforms, Editor, Path, Range } from 'slate';

// If our selection ends up inside of a matching node, move the selection so that it completely
// surrounds the matching node.
// Usually, this is used in the `onChange` callback for a plugin.
export default function ensureFullSelection(editor, matcher) {
  const { selection } = editor;

  let nodePaths = [];
  for (const ip of Editor.nodes(editor, {
    match: n => matcher(n),
  })) {
    nodePaths.push(ip);
  }

  if (!nodePaths.length) {
    return;
  }

  // In the case of a collapsed selection, startInline and endInline will be the same.
  // If the selection starts and ends in two different nodes, then they will be different.
  // Doesn't really affect the logic in any way though.
  const [firstNode, firstPath] = nodePaths[0];
  const [lastNode, lastPath] = _.last(nodePaths);

  const start = Range.start(selection);
  const end = Range.end(selection);

  const firstNodeText = firstNode.text != null ? firstNode.text : firstNode.children[0].text;
  const lastNodeText = lastNode.text != null ? lastNode.text : lastNode.children[0].text;

  // We consider the starts and ends to be "inside" if they're not at the edges.. e.g. "|text" is not inside,
  // but "t|ext" is.
  const isStartInNode =
    start.offset !== 0 && start.offset !== firstNodeText.length && Path.compare(start.path, firstPath) === 0;

  const isEndInNode = end.offset !== 0 && Path.compare(end.path, lastPath) === 0;

  // The update selection should include the node at the beginning and end of the selection (if the selection
  // is collapsed, these will be the same node).
  const updatedSelection = produce(selection, draft => {
    const isForward = Range.isForward(selection);
    if (isStartInNode) {
      draft[isForward ? 'anchor' : 'focus'].offset = 0;
    }
    if (isEndInNode) {
      draft[isForward ? 'focus' : 'anchor'].offset = lastNodeText.length;
    }
  });

  // If the selection hasn't changed, then we don't need to force the issue
  if (Range.equals(updatedSelection, selection)) {
    return;
  }

  Transforms.setSelection(editor, updatedSelection);
  return;
}
